import FileSize from "../../components/commonComponent/FileSize";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import SiteHeader from "../landing/components/Header/SiteHeader";
import Footer from "../landing/components/Footer/Footer";
import useFocusPageStart from "../../hooks/useFocusPageStart";
import Breadcrumb from "../schemes/components/Breadcrumb/Breadcrumb";
import Sidebar from "../schemes/components/Sidebar/Sidebar";
import achievementMenuItems from "./data/achievementMenuItems";
import { getAchievementSection } from "./data/achievementDocuments";
import {
  PdfBadge,
  PdfPreviewModal,
} from "../rightToInformation/components/RtiContent/PdfDocumentTable";

const CURRENT_MPR_YEAR = 2026;

const PageTitle = ({ label }) => {
  const title = label;

  return (
    <h1 className="m-0 text-[20px] font-bold leading-[1.2] uppercase tracking-[1px] text-[#1b3a6b] md:text-[23px] first-letter:text-[#ff9933]">
      {title}
    </h1>
  );
};

const getDocumentYear = (fileName) =>
  Number(fileName.match(/\b(\d{4})\b/)?.[1] || 0);

const DocumentTable = ({ documents, onSelectDocument }) => (
  <div className="overflow-x-auto rounded-xl border border-[#d9e4ef]">
    <div data-print-row className="grid min-w-[600px] grid-cols-[72px_minmax(0,1fr)_260px] items-center gap-2 bg-[#dce9fb] px-4 py-3 text-[13px] font-bold text-[#001b33] sm:grid-cols-[88px_minmax(0,1fr)_260px]">
      <span>S.No.</span>
      <span className="text-left">Title</span>
      <span className="text-left">File</span>
    </div>

    {documents.map((document, index) => {
      const documentTitle = document.title;

      return (
        <div key={document.fileName} className="border-t border-[#d9e4ef]">
          <div data-print-row className={`grid min-w-[600px] grid-cols-[72px_minmax(0,1fr)_260px] items-center gap-2 border-b border-[#d9e4ef] px-4 py-2.5 text-[13px] text-[#001b33] sm:grid-cols-[88px_minmax(0,1fr)_260px] ${index % 2 === 0 ? "bg-[#fbfdff]" : "bg-[#f5f9fc]"}`}>
            <span className="font-medium">{String(index + 1).padStart(2, "0")}</span>
            <span className="min-w-0 self-center break-words text-left font-medium">{documentTitle}</span>
            <button
              type="button"
              onClick={() => onSelectDocument({ ...document, title: documentTitle })}
              className="grid w-full grid-cols-[40px_24px_minmax(0,1fr)] items-center gap-2 whitespace-nowrap rounded-[3px] text-left text-[#16376c] outline-none transition-colors hover:text-[#0f8a7e] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0f8a7e] focus-visible:ring-offset-2"
            >
              <span className="text-[15px] font-medium underline underline-offset-2">Open</span>
              <PdfBadge /><FileSize href={document.href} fileName={document.fileName} />


            </button>
          </div>
        </div>
      );
    })}
  </div>
);

const AchievementsPage = () => {
  const navigate = useNavigate();
  const { section } = useParams();
  const activeSection = getAchievementSection(section);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showMprArchive, setShowMprArchive] = useState(false);

  const isMprSection = activeSection.id === "monthly-progress-reports";
  const currentDocuments = isMprSection
    ? activeSection.documents.filter((document) => getDocumentYear(document.fileName) === CURRENT_MPR_YEAR)
    : activeSection.documents;
  const archivedDocuments = isMprSection
    ? activeSection.documents.filter((document) => getDocumentYear(document.fileName) !== CURRENT_MPR_YEAR)
    : [];

  useEffect(() => {
    setShowMprArchive(false);
  }, [activeSection.id, section]);

  useFocusPageStart(section || activeSection.id);

  const handleSelectSection = (sectionId) => {
    navigate(`/achievements/${sectionId}`);
  };

  return (
    <div className="flex min-h-screen flex-col bg-[#f5f5f5]">
      <SiteHeader className="shadow-[0_2px_4px_rgba(0,0,0,0.1)]">
        <div className="z-[1000] px-4 pt-2 pb-3 md:px-20 md:pb-4">
          <Breadcrumb
            items={[
              { label: "Home", path: "/" },
              { label: "Achievements", path: "/achievements" },
              {
                label: activeSection.title,
                path: `/achievements/${activeSection.id}`,
              },
            ]}
          />
        </div>
      </SiteHeader>

      <main id="main-content" data-readable-content tabIndex={-1} className="flex-1 pb-16 outline-none md:pb-24">
        <div className="px-4 pt-7 pb-3.5 md:px-20 md:pb-[18px]">
          <PageTitle label={activeSection.title} />
        </div>

        <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-5 p-5 md:p-10 lg:flex-row lg:gap-[30px]">
          <Sidebar
            activeSection={activeSection.id}
            onSelectSection={handleSelectSection}
            menuItems={achievementMenuItems}
          />

          <section className="min-w-0 flex-1 rounded-2xl bg-white p-4 shadow-[0_10px_30px_rgba(0,0,0,0.04)] sm:p-6">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-[22px] font-bold text-[#006461]">{activeSection.title}</h2>
              {isMprSection && archivedDocuments.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowMprArchive((isVisible) => !isVisible)}
                  className="rounded-lg border border-[#006461] px-4 py-2 text-sm font-semibold text-[#006461] transition-colors hover:bg-[#006461] hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0f8a7e] focus-visible:ring-offset-2"
                  aria-expanded={showMprArchive}
                >
                  {showMprArchive ? "Hide Archive" : "Archive"}
                </button>
              )}
            </div>

            <DocumentTable
              documents={currentDocuments}
              onSelectDocument={setSelectedDocument}
            />

            {isMprSection && showMprArchive && archivedDocuments.length > 0 && (
              <div className="mt-6">
                <h3 className="mb-3 text-lg font-bold text-[#006461]">
                  Archived Monthly Progress Reports
                </h3>
                <DocumentTable
                  documents={archivedDocuments}
                  onSelectDocument={setSelectedDocument}
                />
              </div>
            )}
          </section>
        </div>
      </main>
      <Footer />
      {selectedDocument && (
        <PdfPreviewModal
          pdfDocument={selectedDocument}
          onClose={() => setSelectedDocument(null)}
        />
      )}
    </div>
  );
};

export default AchievementsPage;
